import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { Globe, Search, ExternalLink, Shield, Clock, CheckCircle2 } from "lucide-react";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const VERIF_CONFIG = {
  self_reported: { label: "Self-reported", class: "bg-slate-100 text-slate-600 border-slate-200", icon: null },
  platform_verified: { label: "Calculated Estimate", class: "bg-blue-50 text-blue-700 border-blue-200", icon: "shield" },
  third_party_verified: { label: "Calculated Estimate", class: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: "award" }
};

export default function Registry() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await client.get("/registry");
        setRecords(res.data.records || []);
        setTotal(res.data.total || 0);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const filtered = records.filter(r =>
    r.title?.toLowerCase().includes(search.toLowerCase()) ||
    r.registry_id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
          
      <PageHeader
        title="Registry"
        subtitle="Published and verified climate credentials."
      />
        <button onClick={() => window.open("/registry", "_blank")} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50">
          <ExternalLink size={14} /> Public Registry
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <MetricCard label="Total Published" value={total} color="text-slate-900" />
        <MetricCard label="Verified" value={records.filter(r => r.status === "published").length} color="text-[#059669]" />
        <MetricCard label="Expired" value={records.filter(r => r.status === "expired").length} color="text-slate-400" className="hidden sm:block" />
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search registry..." className="w-full h-9 pl-8 pr-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" data-testid="registry-search" />
      </div>

      <SectionCard>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="registry-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Registry ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Published</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(3)].map((_, i) => <tr key={i}><td colSpan={6} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center">
                    <Globe size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-500">No published registry records</p>
                    <p className="text-xs text-slate-400 mt-1">Issue and publish reports to appear here.</p>
                  </td>
                </tr>
              ) : filtered.map(r => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`registry-row-${r.id}`}>
                  <td className="px-5 py-4">
                    <span className="text-xs font-mono text-[#059669] font-semibold">{r.registry_id}</span>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm font-medium text-slate-900">{r.title}</p>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500 capitalize hidden sm:table-cell">{r.type?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-4">
                    <div className="space-y-1">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${r.status === "published" ? "bg-emerald-50 text-emerald-700" : r.status === "expired" ? "bg-slate-50 text-slate-400" : "bg-amber-50 text-amber-700"}`}>
                        {r.status === "published" && <Shield size={11} />}
                        {r.status}
                      </span>
                      {r.verification_level && VERIF_CONFIG[r.verification_level] && (
                        <div>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${VERIF_CONFIG[r.verification_level].class}`}>
                            {r.verification_level === "platform_verified" && <Shield size={9} />}
                            {VERIF_CONFIG[r.verification_level].label}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500 hidden md:table-cell">
                    {r.issue_date ? new Date(r.issue_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500 hidden md:table-cell">
                    {r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </SectionCard>
    </div>
  );
}
